import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { getXHashtagList } from '@opm/services';
import type { IXHashtagApiFilter, IXHashtagListResponse } from '../types';

const EMPTY_RESPONSE: IXHashtagListResponse = {
  group_by: 'date',
  total: 0,
  list: [],
};

interface IUseXHashtagListArgs {
  filter: IXHashtagApiFilter | null;
  onHasDataDate?: (date: string) => void;
  onHasDownloadChange?: (hasDownload: boolean) => void;
  downloadRef?: MutableRefObject<(() => void) | null>;
  /** 仅首次列表请求完成后可触发 onHasDataDate */
  isFirstRequestRef?: MutableRefObject<boolean>;
}

export function useXHashtagList({
  filter,
  onHasDataDate,
  onHasDownloadChange,
  downloadRef,
  isFirstRequestRef,
}: IUseXHashtagListArgs) {
  const [data, setData] = useState<IXHashtagListResponse>(EMPTY_RESPONSE);
  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const onHasDataDateRef = useRef(onHasDataDate);
  onHasDataDateRef.current = onHasDataDate;

  const request = useCallback(() => {
    if (!filter) {
      setData(EMPTY_RESPONSE);
      return;
    }

    wrapperCancelRequest(getXHashtagList, filter)
      .then((res: any) => {
        if (!res) return;
        const total = res.total ?? 0;
        setData({
          group_by: res.group_by || filter.group_by,
          total,
          list: Array.isArray(res.list) ? res.list : [],
          has_data_date: res.has_data_date,
        });

        const isFirstRequest = isFirstRequestRef?.current ?? false;
        if (isFirstRequestRef) {
          isFirstRequestRef.current = false;
        }
        if (isFirstRequest && res.has_data_date) {
          onHasDataDateRef.current?.(res.has_data_date);
        }
      })
      .catch(() => {});
  }, [filter, wrapperCancelRequest]);

  useEffect(() => {
    const timer = setTimeout(() => {
      request();
    }, 0);
    return () => clearTimeout(timer);
  }, [request]);

  const download = useCallback(() => {
    if (!filter) return;
    gotoDownload({
      key: 'MarketingHashtagXDownload',
      fileName: `Hashtag_X_${DBO_TM.timeTitle(filter as any)}`,
      filter,
    });
  }, [filter]);

  useEffect(() => {
    onHasDownloadChange?.(!Boolean(loading) && data.total > 0);
  }, [data.total, loading, onHasDownloadChange]);

  useEffect(() => {
    if (downloadRef) {
      downloadRef.current = download;
    }
  }, [download, downloadRef]);

  return {
    data,
    total: data.total,
    loading,
    download,
    groupBy: data.group_by,
  };
}
